import './perks-posts-sidebar.component.scss';

import dayjs from 'dayjs';
import { h } from 'preact';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { selectAccountSettings } from '../../../auth/store/auth.selectors';
import { IconPencil } from '../../../common/icons/icon-pencil';
import { useFeatureFlag } from '../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../i18n';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import { isContractFailed } from '../../../membership/utils/membership.utlis';
import { useAppSelector } from '../../../store/hooks';
import { linkify } from '../../../utils/a11y.utils';
import { selectUser } from '../../store/perks.selectors';
import { useDisplayIdentity } from '../../use-display-identity.hook';
import { PerksAvatar } from '../perks-avatar/perks-avatar.component';
import { PROFILE_BADGE_SIZE_PX } from '../perks-badge/perks-badge.component';
import { PerksPopularPosts } from '../perks-popular-posts/perks-popular-posts.component';

interface Props {
  /**
   * Opens the submission form. The feed toggles it in place; a post's own page has no form to
   * open, so it routes back to the feed and leaves the intent behind (see pending-submission).
   */
  onCreatePost: () => void;
}

/**
 * The profile card + Popular Posts column. It rides alongside the feed and alongside a single
 * post, so opening a post no longer drops the column and stretches the post to full width.
 */
export const PerksPostsSidebar = ({ onCreatePost }: Props) => {
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const memberships = useAppSelector(selectMemberships);
  const memberSince = memberships?.[0]?.contract?.createdAt ?? user?.createdAt;
  const [, setLocation] = useHashLocation();
  const accountSettingsResp = useAppSelector(selectAccountSettings);
  // the author's own name and artwork stand in for the fan profile once a user is linked to one
  const identity = useDisplayIdentity(user);
  // artists and moderators publish directly, so the entry point says Create rather than Submit --
  // behind community-official-authors, like the attribution itself
  const officialAuthorsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_OFFICIAL_AUTHORS);
  const skipsReview = officialAuthorsEnabled && (!!user?.officialAuthor || !!user?.moderator);
  // members only: the memberships list is the account's programs; a member is a user holding a
  // live contract on one of them (payment-failed contracts have already lost access)
  const isMember = !!memberships?.some((m) => !!m.contract && !isContractFailed(m.contract));
  // SNGL-7327: the artist is not a fan. Publishing to their own community is not a "community
  // submission", so neither gate on the fan path applies to them -- the membership contract and the
  // enableCommunitySubmissions setting both exist to control what fans can send in for review.
  // the submission endpoint 403s without community-submissions, so no entry point without it --
  // artists and moderators on a store that never enabled submissions post from the dashboard
  const submissionsEnabled = useFeatureFlag(FeatureFlagName.COMMUNITY_SUBMISSIONS);
  const canSubmitPosts =
    !!user &&
    submissionsEnabled &&
    (skipsReview ||
      (!!accountSettingsResp?.accountSettings?.enableCommunitySubmissions && isMember));

  const handleUserInfoClick = () => {
    // a linked author is shown as the author, so do not drop them into edit mode to add a fan photo
    const showEdit = user && !user?.imageUrl && !identity.official;
    setLocation(`/my-account${showEdit ? '?showEdit=true' : ''}`);
  };

  return (
    <div class="single-posts-sidebar single-posts-right-column single-posts-sticky">
      {user && (
        <div class="single-user-info" {...linkify(handleUserInfoClick, t('perks.myAccount'))}>
          <div class="single-avatar-wrapper">
            <PerksAvatar user={user} size={100} badgeSize={PROFILE_BADGE_SIZE_PX} />
            {/* the pencil offers to upload a fan photo; for a linked author nobody would see it */}
            {!user.imageUrl && !identity.official && (
              <div class="single-avatar-hover">
                <IconPencil />
              </div>
            )}
          </div>

          {/* a linked author always has a name to show, even when the fan profile has no username */}
          {(identity.official || !!user.username) && <h4 class="h4 single-h4">{identity.name}</h4>}

          {/* "Member since" falls back to the fan signup date, which reads as a fan stat on an
              artist who holds no membership */}
          {memberSince && !identity.official && (
            <div class="member-since">
              <small>{t('perks.memberSince')}</small>
              <h5 class="h5 single-h5">{dayjs(memberSince).format('MMMM YYYY')}</h5>
            </div>
          )}

          {canSubmitPosts && (
            <button
              class="button btn single-submit-post-card"
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onCreatePost();
              }}
            >
              {t(skipsReview ? 'perks.submission.createPost' : 'perks.submission.submitPost')}
            </button>
          )}
        </div>
      )}

      <div class="single-popular-posts">
        <PerksPopularPosts />
      </div>
    </div>
  );
};
